import React from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

import { config } from '@config/pendo.config.json';
import AiGeneratingLoader from 'lecture_pages/components/left-panel/novo-ai/components/shared/ai-generating-loader';
import useStaggeredPhrases from 'shared/hooks/use-staggered-phrases';
import { gray2 } from 'styles/global_defaults/colors';
import {
  boldFontWeight,
  openSans,
  textLargeLineHeight,
  textMediumFontSize,
  textMediumLineHeight,
} from 'styles/global_defaults/fonts';
import {
  doubleSpacing,
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';

const styles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${doubleSpacing}px ${largeSpacing}px;
  gap: ${standardSpacing}px;

  .aniText {
    line-height: ${textLargeLineHeight}px;
  }

  .generating-description {
    font-family: ${openSans};
    font-weight: ${boldFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${textMediumLineHeight}px;
    color: ${gray2};
    margin: 0;
  }

  .generating-steps {
    display: flex;
    flex-direction: column;
    gap: ${halfSpacing}px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .generating-step {
    font-family: ${openSans};
    font-size: ${textMediumFontSize}px;
    line-height: ${textMediumLineHeight}px;
    color: ${gray2};
  }

  .indicator {
    display: inline-block;
    animation: indicator-fade-in 400ms ease-in;
  }

  .indicator-done {
    margin-left: ${quarterSpacing}px;
  }

  @keyframes indicator-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    .indicator {
      animation: none;
    }
  }
`;

const GeneratingState: React.FC = () => {
  const dataQa = config.pendo.submissionView.aiInsights.generatingState;
  const stepDataQa = config.pendo.submissionView.aiInsights.generatingPhrase;

  const steps = [
    t.SUBMISSIONS.AI_ASSIGNMENT.GENERATING_STATE.STEP_1(),
    t.SUBMISSIONS.AI_ASSIGNMENT.GENERATING_STATE.STEP_2(),
    t.SUBMISSIONS.AI_ASSIGNMENT.GENERATING_STATE.STEP_3(),
  ];
  const pendingIndicator = t.SUBMISSIONS.AI_ASSIGNMENT.GENERATING_STATE.STEP_PENDING_INDICATOR();
  const completedIndicator = t.SUBMISSIONS.AI_ASSIGNMENT.GENERATING_STATE.STEP_COMPLETED_INDICATOR();
  const completedCount = useStaggeredPhrases(steps).length;

  return (
    <div css={styles} data-qa={dataQa}>
      <AiGeneratingLoader
        customTitle={t.SUBMISSIONS.AI_ASSIGNMENT.GENERATING_STATE.TITLE()}
        showLoaderDescription={false}
      />
      <p className='generating-description'>
        {t.SUBMISSIONS.AI_ASSIGNMENT.GENERATING_STATE.DESCRIPTION()}
      </p>
      <ul className='generating-steps' aria-live='polite'>
        {steps.map((step, i) => {
          const isDone = i < completedCount;
          return (
            <li
              key={step}
              className='generating-step'
              data-qa={`${stepDataQa}-${i + 1}`}
            >
              {step}
              <span
                key={isDone ? 'done' : 'pending'}
                className={isDone ? 'indicator indicator-done' : 'indicator'}
              >
                {isDone ? completedIndicator : pendingIndicator}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default GeneratingState;
